import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * GSAP Section Drawer
 * 
 * Shows a fixed element (drawer) when the user scrolls into a specific section.
 * 
 * Usage:
 * Add class `.section-drawer` to your fixed drawer element.
 * Optional: `data-target="#target-section-id"` to specify the trigger section. 
 *           If omitted, it will look for the closest parent `section`.
 * Optional: `data-offset="10%"` to control when it triggers (default: 10% of viewport height).
 * 
 * The drawer styles are handled by `_section-drawer.scss`.
 */
export default function initGsapSectionDrawer() {
    const drawers = document.querySelectorAll('.section-drawer');

    drawers.forEach(drawer => {
        const sectionSelector = drawer.dataset.target;
        
        // If no selector provided, try to find the closest section parent
        const section = sectionSelector ? document.querySelector(sectionSelector) : drawer.closest('section');

        if (!section) {
            console.warn(`GSAP Section Drawer: Target section not found for drawer`, drawer);
            return;
        }

        const offset = drawer.dataset.offset || "10%";

        // Move drawer to body to ensure position: fixed works relative to viewport
        // regardless of parent transforms
        document.body.appendChild(drawer);

        // Initial state: Hidden (Translated down 100% and invisible)
        gsap.set(drawer, { 
            yPercent: 100, 
            autoAlpha: 0 
        });

        ScrollTrigger.create({
            trigger: section,
            start: `top bottom-=${offset}`, 
            end: "bottom bottom", 
            refreshPriority: -1, // Wait for pinned sections to settle
            
            onEnter: () => animateDrawer(drawer, "in"),
            onLeave: () => animateDrawer(drawer, "out"),
            onEnterBack: () => animateDrawer(drawer, "in"),
            onLeaveBack: () => animateDrawer(drawer, "out"),
        });
    });
}

function animateDrawer(element, direction) {
    const isIn = direction === "in";
    
    gsap.to(element, {
        yPercent: isIn ? 0 : 100,
        autoAlpha: isIn ? 1 : 0,
        duration: 0.4,
        ease: isIn ? "power2.out" : "power2.in",
        overwrite: true
    });
}